import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { PasswordInput } from '../components/ui/password-input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader } from '../components/ui/card';
import { useAuth } from '../contexts/AuthContext';

const LOGIN_LOGO_SRC = '/login-assets/bright-express-login-logo.png';

function LoginLogo({ className }: { className?: string }) {
  return (
    <img
      src={LOGIN_LOGO_SRC}
      alt="bright.express"
      className={className}
    />
  );
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setMessage(null);
    setSubmitting(true);

    try {
      await login(email, password);
    } catch (err: unknown) {
      setMessage(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 flex min-w-full min-h-screen overflow-hidden">
      {/* Mobile: full-width brand background */}
      <div className="absolute inset-0 z-0 xl:hidden login-sweep-bg" />

      <div className="relative z-10 flex w-full min-w-0 h-full flex-col xl:flex-row xl:items-stretch flex-1">
        {/* Left brand panel — logo centered on desktop only */}
        <div className="hidden xl:flex xl:w-[70%] xl:min-w-0 xl:h-full xl:relative xl:shrink-0">
          <div className="absolute inset-0 login-sweep-bg" />
          <div className="relative z-10 flex h-full w-full items-center justify-center px-12">
            <LoginLogo className="w-[min(420px,22vw)] max-w-full h-auto object-contain" />
          </div>
        </div>

        {/* Right: login form — light panel on desktop; transparent on mobile so logo sits on sweep */}
        <div className="flex flex-1 flex-col xl:h-full xl:w-[30%] xl:shrink-0 relative z-10 items-center justify-center px-4 py-10 xl:bg-background">
          <div className="flex w-full max-w-sm flex-col items-stretch">
            {/* Mobile / tablet: logo above the card, on the dark sweep */}
            <div className="mb-8 flex justify-center xl:hidden">
              <LoginLogo className="w-[min(300px,78vw)] h-auto object-contain" />
            </div>

            <Card className="w-full rounded-lg border-0 bg-card shadow-[0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)]">
              <CardHeader className="p-6 pb-2">
                <h2 className="text-lg font-semibold text-center tracking-tight">Log in</h2>
              </CardHeader>
              <CardContent className="p-6 pt-2">
                <form onSubmit={handleSubmit} className="space-y-6" noValidate>
                  {message && (
                    <p
                      role="alert"
                      className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
                    >
                      {message}
                    </p>
                  )}
                  <div className="grid gap-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      disabled={submitting}
                      placeholder="you@example.com"
                      autoComplete="username"
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="password">Password</Label>
                    <PasswordInput
                      id="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      disabled={submitting}
                      autoComplete="current-password"
                    />
                  </div>
                  <Button type="submit" className="w-full min-w-[200px]" disabled={submitting}>
                    {submitting ? 'Signing in...' : 'Login'}
                  </Button>
                </form>
                <div className="mt-4 text-center">
                  <a
                    href="/forgot"
                    className="text-sm text-muted-foreground hover:text-primary hover:underline"
                  >
                    Forgot password?
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
